import { Button } from "@/core/components/ui/button";
import { cn } from "@/core/utils/func/cn";
import { CheckCircle } from "lucide-react";
import { ButtonHTMLAttributes, forwardRef } from "react";

export interface ValidationButtonProps
  extends ButtonHTMLAttributes<HTMLButtonElement> {
  /**
   * Texte du bouton
   */
  children: React.ReactNode;
  /**
   * Activer l'animation pulse pour attirer l'attention
   * @default false
   */
  withPulse?: boolean;
  /**
   * Activer l'indicateur animé (point jaune)
   * @default true
   */
  withIndicator?: boolean;
  /**
   * Couleur du gradient
   * @default "green"
   */
  variant?: "green" | "blue" | "purple" | "orange" | "red";
  /**
   * Icône personnalisée (par défaut CheckCircle)
   */
  icon?: React.ComponentType<{ className?: string }>;
}

const gradientVariants = {
  green: {
    base: "from-success to-success/80",
    hover: "hover:from-success/90 hover:to-success/70",
    shadow: "hover:shadow-success/40",
  },
  blue: {
    base: "from-info to-info/80",
    hover: "hover:from-info/90 hover:to-info/70",
    shadow: "hover:shadow-info/40",
  },
  purple: {
    base: "from-purple-600 to-purple-700 dark:from-purple-500 dark:to-purple-600",
    hover: "hover:from-purple-700 hover:to-purple-800 dark:hover:from-purple-600 dark:hover:to-purple-700",
    shadow: "hover:shadow-purple-400/50 dark:hover:shadow-purple-500/30",
  },
  orange: {
    base: "from-warning to-warning/80",
    hover: "hover:from-warning/90 hover:to-warning/70",
    shadow: "hover:shadow-warning/40",
  },
  red: {
    base: "from-destructive to-destructive/80",
    hover: "hover:from-destructive/90 hover:to-destructive/70",
    shadow: "hover:shadow-destructive/40",
  },
};

export const ValidationButton = forwardRef<
  HTMLButtonElement,
  ValidationButtonProps
>(
  (
    {
      children,
      className,
      withPulse = false,
      withIndicator = true,
      variant = "green",
      icon: Icon = CheckCircle,
      ...props
    },
    ref
  ) => {
    const gradients = gradientVariants[variant];

    return (
      <Button
        ref={ref}
        size="lg"
        className={cn(
          // Styles de base
          "px-8 py-4 font-bold text-white border-0 shadow-xl transition-all duration-300 relative",
          // Gradient background
          `bg-gradient-to-r ${gradients.base}`,
          // Hover effects
          `${gradients.hover} hover:scale-105 hover:shadow-2xl ${gradients.shadow}`,
          // Animation conditionnelle
          withPulse && "animate-pulse",
          // Focus behavior
          "focus:animate-none hover:animate-none",
          className
        )}
        {...props}
      >
        {/* Icône avec cercle */}
        <div className="flex justify-center items-center mr-3 w-6 h-6 rounded-full bg-white/20">
          <Icon className="w-4 h-4" />
        </div>

        {/* Contenu du bouton */}
        {children}

        {/* Indicateur animé optionnel */}
        {withIndicator && (
          <div className="absolute -top-1 -right-1 w-3 h-3 bg-warning rounded-full animate-ping"></div>
        )}
      </Button>
    );
  }
);

ValidationButton.displayName = "ValidationButton";
